import APP from '$/api/api';
import { canvasRequest } from '$/api/canvasRquest';
import type {
  V3CanvasHomeFeaturedCaseItem,
  V3CanvasHomeFeaturedCastingIp,
  V3CanvasHomeFeaturedId,
  V3CanvasHomeFeaturedIpDisplayMode,
  V3CanvasHomeFeaturedListParams,
} from './type';

export interface FeaturedPreviewParams extends V3CanvasHomeFeaturedListParams {
  featured_id: V3CanvasHomeFeaturedId;
}

export interface FeaturedPreviewData {
  presentation?: { workflow_public?: boolean; };
  featured_id: V3CanvasHomeFeaturedId;
  featured_version: number;
  case_id: V3CanvasHomeFeaturedId;
  work_id: V3CanvasHomeFeaturedId;
  category_id: V3CanvasHomeFeaturedId;
  case: V3CanvasHomeFeaturedCaseItem & { graph_json?: string; };
  casting_ip_count: number;
  casting_ips: V3CanvasHomeFeaturedCastingIp[] | null;
  copy_allowed: boolean;
  ip_display_mode: V3CanvasHomeFeaturedIpDisplayMode;
  copy_disabled_reason?: string;
}

export interface FeaturedCopyParams {
  featured_id: V3CanvasHomeFeaturedId;
  featured_version: number;
  idempotency_key: string;
  room_id: string;
}

export interface FeaturedCopyData {
  featured_id: V3CanvasHomeFeaturedId;
  case_id: V3CanvasHomeFeaturedId;
  work_id: V3CanvasHomeFeaturedId;
  project_id: string;
  room_id: string;
  canvas_id?: string;
  graph_copied: boolean;
  written_to_mine: boolean;
  ip_bindings: { source_ip_id: number; bound_ip_id: number; }[] | null;
  invalid: { ip_id: number; reason: string; }[] | null;
}

export interface FeaturedIpShowcaseData {
  ip: {
    ip_id: number;
    uid: V3CanvasHomeFeaturedId;
    asset_type: 'official' | 'personal';
    name: string;
    summary: string;
  };
  project_total: number;
  work_total: number;
  is_owner: boolean;
}

export enum FeaturedErrorCode {
  NotFound = 15401,
  Unavailable = 15402,
  CopyForbidden = 15403,
  InvalidIp = 15404,
  InvalidParams = 15405,
}

/** 保留 WebAPI 错误码，供本地校验与服务端失败复用同一展示逻辑。 */
export class FeaturedRequestError extends Error {
  data: { code: FeaturedErrorCode; };

  /** 从本地校验失败构造与 WebAPI 一致的业务错误。 */
  constructor(code: FeaturedErrorCode) {
    super(`Featured request failed: ${code}`);
    this.data = { code };
  }
}

/** 同时识别 HTTP 错误和 HTTP 200 中的 WebAPI 业务错误。 */
export function getFeaturedErrorCode(error: unknown): number | undefined {
  const value = error as { response?: { data?: { code?: number; }; }; data?: { code?: number; }; };
  return value?.response?.data?.code ?? value?.data?.code;
}

/** 匿名预览只使用 Query，投放上下文由调用方按入口透传。 */
export async function getFeaturedPreview(params: FeaturedPreviewParams): Promise<FeaturedPreviewData> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasHomeFeaturedPreviewV3,
    method: 'GET',
    params,
    skipErrorRedirect: true,
  });
  if (response.status === 200 && response.data.code === 0) return response.data.data;
  return Promise.reject(response);
}

/** 在公共参数补齐后输出数字型 featured_id，避免路由或无损解析产生的字符串进入 JSON Body。 */
function serializeFeaturedCopyRequest(data: FeaturedCopyParams & Record<string, unknown>): string {
  const value = data.featured_id;
  const id = String(value);
  const maxId = '18446744073709551615';
  if ((typeof value !== 'string' && typeof value !== 'number')
    || (typeof value === 'number' && !Number.isSafeInteger(value))
    || !/^[1-9]\d*$/.test(id) || id.length > maxId.length
    || (id.length === maxId.length && id > maxId)) {
    throw new FeaturedRequestError(FeaturedErrorCode.InvalidParams);
  }
  const { featured_id: _id, ...rest } = data;
  const body = JSON.stringify(rest);
  // 仅拼接已校验的十进制数字，不替换其他字段内容，也不经过 Number 导致精度损失。
  return `${body.slice(0, -1)}${body === '{}' ? '' : ','}"featured_id":${id}}`;
}

/** 一次性复制画布并写入我的项目；重试必须复用同一个幂等键。 */
export async function copyFeaturedCase(data: FeaturedCopyParams): Promise<FeaturedCopyData> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasHomeFeaturedCopyV3,
    method: 'POST',
    data,
    transformRequest: [serializeFeaturedCopyRequest],
    headers: { 'Content-Type': 'application/json' },
    skipErrorRedirect: true,
  });
  if (response.status === 200 && response.data.code === 0) return response.data.data;
  return Promise.reject(response);
}

/** 出演 IP 悬浮时按需取展示信息，官方 IP 支持匿名读取。 */
export async function getFeaturedIpShowcase(ipId: number): Promise<FeaturedIpShowcaseData> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasIpShowcaseDetailV3,
    method: 'GET',
    params: { ip_id: ipId },
    skipErrorRedirect: true,
  });
  if (response.status === 200 && response.data.code === 0) return response.data.data;
  return Promise.reject(response);
}
